import { GroupedSelectOption, SelectOption } from '@scaleaq/scaleui';
import {
  IACCurrentReading,
  IACPowerPReading,
  IACPowerQReading,
  IACPowerSReading,
  IACVoltageReading,
  IBatteryEstimatedChargeTime,
  IBatteryEstimatedDischargeTime,
  IBatteryLevelReading,
  IBatterySoCReading,
  IBatterySoHReading,
  IBrightnessPercentageReading,
  IDCCurrentReading,
  IDCPowerReading,
  IDCVoltageReading,
  IDepthReading,
  IEngineSpeedRpmReading,
  IFadeTimeReading,
  IFuelLiveReading,
  IIsDeviceOn,
  InventoryProvider,
  InventoryTree,
  IO2mgReading,
  IO2PercentageReading,
  IO2uMolReading,
  IPitchReading,
  IRollReading,
  ISalinityReading,
  ITankLevelRawReading,
  ITankLevelReading,
  ITemperatureReading,
  IThreePhaseCurrentL1Reading,
  IThreePhaseCurrentL2Reading,
  IThreePhaseCurrentL3Reading,
  IThreePhaseVoltageL12Reading,
  IThreePhaseVoltageL13Reading,
  IThreePhaseVoltageL23Reading,
  IThrusterSpeed,
  IThrusterTargetSpeedChanged,
  IYawReading,
  MappingCollections,
  SensorInventory,
  type InventoryRepoPlus,
} from '@scaleaq/sitecontrol-lib';

import { localeBaseCompare, sortByName } from './sort.ts';
import { capitalizeAndSplitOnUpperCase } from './string.ts';

export const getValueByProvider = (data: unknown, provider: InventoryProvider) => {
  switch (provider) {
    case InventoryProvider.O2Percentage:
      return (data as IO2PercentageReading).o2Percentage;

    case InventoryProvider.O2uMol:
      return (data as IO2uMolReading).o2uMol;

    case InventoryProvider.O2mg:
      return (data as IO2mgReading).o2mg;

    case InventoryProvider.Salinity:
      return (data as ISalinityReading).salinity;

    case InventoryProvider.Temperature:
      return (data as ITemperatureReading).temperature;

    case InventoryProvider.Depth:
      return (data as IDepthReading).depth;

    case InventoryProvider.VoltageDC:
      return (data as IDCVoltageReading).voltage;

    case InventoryProvider.CurrentDC:
      return (data as IDCCurrentReading).current;

    case InventoryProvider.PowerDC:
      return (data as IDCPowerReading).power;

    case InventoryProvider.SoC:
      return (data as IBatterySoCReading).soC;

    case InventoryProvider.SoH:
      return (data as IBatterySoHReading).soH;

    case InventoryProvider.VoltageAC:
      return (data as IACVoltageReading).voltage;

    case InventoryProvider.CurrentAC:
      return (data as IACCurrentReading).current;

    case InventoryProvider.PowerAC:
      return (data as IACPowerPReading).powerP;

    case InventoryProvider.BatteryPercentage:
      return (data as IBatteryLevelReading).batteryLevel;

    case InventoryProvider.Pitch:
      return (data as IPitchReading).pitch;

    case InventoryProvider.Yaw:
      return (data as IYawReading).yaw;

    case InventoryProvider.Roll:
      return (data as IRollReading).roll;

    case InventoryProvider.EngineSpeedRpm:
      return (data as IEngineSpeedRpmReading).speedRpm;

    case InventoryProvider.LiveFuelConsumption:
      return (data as IFuelLiveReading).fuelLive;

    case InventoryProvider.BrightnessPercentage:
      return (data as IBrightnessPercentageReading).brightnessPercentage;
    case InventoryProvider.IsDeviceOn:
      return (data as IIsDeviceOn).isOn;

    case InventoryProvider.FadeTime:
      return (data as IFadeTimeReading).fadeTime;

    case InventoryProvider.PowerP:
      return (data as IACPowerPReading).powerP;

    case InventoryProvider.PowerQ:
      return (data as IACPowerQReading).powerQ;

    case InventoryProvider.PowerS:
      return (data as IACPowerSReading).powerS;

    case InventoryProvider.VoltageL12:
      return (data as IThreePhaseVoltageL12Reading).voltageL12;

    case InventoryProvider.VoltageL23:
      return (data as IThreePhaseVoltageL23Reading).voltageL23;

    case InventoryProvider.VoltageL13:
      return (data as IThreePhaseVoltageL13Reading).voltageL13;

    case InventoryProvider.CurrentL1:
      return (data as IThreePhaseCurrentL1Reading).currentL1;

    case InventoryProvider.CurrentL2:
      return (data as IThreePhaseCurrentL2Reading).currentL2;

    case InventoryProvider.CurrentL3:
      return (data as IThreePhaseCurrentL3Reading).currentL3;

    case InventoryProvider.ThrusterSpeed:
      return (data as IThrusterSpeed).speed;

    case InventoryProvider.ThrusterTargetSpeed:
      return (data as IThrusterTargetSpeedChanged).targetSpeed;

    case InventoryProvider.DischargeTime:
      return (data as IBatteryEstimatedDischargeTime).estimatedDischargeTime;

    case InventoryProvider.ChargeTime:
      return (data as IBatteryEstimatedChargeTime).estimatedChargeTime;

    case InventoryProvider.TankLevel:
      return (data as ITankLevelReading).tankLevel;

    case InventoryProvider.TankLevelRaw:
      return (data as ITankLevelRawReading).tankLevelRaw;

    case InventoryProvider.VortexPenMode:
    case InventoryProvider.Voltage3Phase:
    case InventoryProvider.Current3Phase:
      throw new Error(`${provider} is not supported`);
  }
};

/** Single DFS over the inventory forest; use for O(1) id lookups instead of repeated tree searches. */
export const buildInventoryNodeByIdMap = (
  inventoryList: InventoryTree[] | undefined
): ReadonlyMap<string, InventoryTree> => {
  const byId = new Map<string, InventoryTree>();
  if (!inventoryList?.length) {
    return byId;
  }
  const visit = (node: InventoryTree) => {
    byId.set(node.id, node);
    for (const child of node.inventories) {
      visit(child);
    }
  };
  for (const root of inventoryList) {
    visit(root);
  }
  return byId;
};

type InventoryParentSelectEntry = {
  type: InventoryTree['type'];
  option: SelectOption<string>;
};

/** Leaf nodes are excluded; recursion only follows non-leaf children. */
const collectInventoryParentSelectEntries = (
  inventories: InventoryTree[] | null | undefined
): InventoryParentSelectEntry[] => {
  if (!inventories?.length) {
    return [];
  }

  const collect = (node: InventoryTree): InventoryParentSelectEntry[] => {
    const selfEntry: InventoryParentSelectEntry[] = !node.isLeaf
      ? [{ type: node.type, option: { id: node.id, label: node.name, value: node.id } }]
      : [];
    const eligibleChildren = node.inventories?.filter(child => !child.isLeaf) ?? [];
    const children = eligibleChildren.flatMap(collect);
    return [...selfEntry, ...children];
  };

  return inventories.flatMap(collect);
};

/** Parent dropdown options grouped by inventory type; options within each group are A→Z by name. Leaf nodes are excluded; recursion only follows non-leaf children. */
export const groupInventoryParentSelectOptionsByType = (
  inventories: InventoryTree[] | null | undefined,
  formatTypeLabel?: (type: InventoryTree['type']) => string
): GroupedSelectOption<SelectOption<string>>[] => {
  const grouped = collectInventoryParentSelectEntries(inventories).reduce(
    (acc, { type, option }) => {
      const options = acc.get(type) ?? [];
      options.push(option);
      acc.set(type, options);
      return acc;
    },
    new Map<InventoryTree['type'], SelectOption<string>[]>()
  );

  return [...grouped.entries()]
    .map(([type, options]) => ({
      label: formatTypeLabel?.(type) ?? type,
      options: [...options].sort((a, b) => localeBaseCompare(a.label, b.label)),
    }))
    .sort((a, b) => localeBaseCompare(a.label, b.label));
};

export const findSensorInInventory = (
  inventoryList: InventoryTree[] | undefined,
  sensorId?: string
): InventoryTree | null => {
  if (!sensorId || !inventoryList) return null;

  const search = (node: InventoryTree): InventoryTree | null => {
    if (node.id === sensorId) return node;
    for (const child of node.inventories) {
      const result = search(child);
      if (result) return result;
    }
    return null;
  };

  for (const root of inventoryList) {
    const result = search(root);
    if (result) return result;
  }

  return null;
};

export const getFriendlyActionName = (action: string) =>
  MappingCollections.inventoryActionInfoPair[action]?.name ?? capitalizeAndSplitOnUpperCase(action);

export const filterAndFlatten = (
  allSensors: InventoryTree[],
  providerSet: string[]
): InventoryTree[] => {
  return [
    ...(allSensors?.filter((s: InventoryTree) =>
      s.providers.find((p: string) => providerSet.includes(p))
    ) || []),
    ...(allSensors
      ?.map((inv: InventoryTree) => filterAndFlatten(inv.inventories, providerSet))
      ?.flat() || []),
  ];
};

export const getAllUniqueProviders = (i: InventoryTree[] | undefined): InventoryProvider[] => {
  if (!i) {
    return [];
  }
  const providerSet = new Set<InventoryProvider>();
  i.map(root => root.providers)
    .flat()
    .forEach(provider => providerSet.add(provider));
  return Array.from(providerSet);
};

export const groupSensorOptionsByType = <T>(
  sensors: SensorInventory[],
  valueMapper: (sensor: SensorInventory) => T
): GroupedSelectOption<SelectOption<T>>[] =>
  Object.entries(
    sortByName(sensors).reduce(
      (acc, sensor) => ({
        ...acc,
        [sensor.type]: [
          ...(acc[sensor.type] || []),
          { id: sensor.id, label: sensor.name, value: valueMapper(sensor) },
        ],
      }),
      {} as Record<string, SelectOption<T>[]>
    )
  )
    .map(([sensorType, sensorArray]) => ({ label: sensorType, options: sensorArray }))
    .sort((a, b) => localeBaseCompare(a.label, b.label));

type PenInventoryFields = {
  fields?: InventoryRepoPlus['fields'];
};

/**
 * Reads a lifecycle flag the way the inventory API does (`bool.TryParse`): casing and surrounding
 * whitespace are irrelevant, and anything that is not a parseable boolean counts as not set. Keeps
 * the pen lists agreeing with the site support the API reports, which decides the Pen Viewer gate.
 */
const isFlagSet = (value: string | undefined): boolean => value?.trim().toLowerCase() === 'true';

export const isDeletedPen = (pen: PenInventoryFields): boolean =>
  isFlagSet(pen.fields?.['Deleted']);

export const isDeactivatedPen = (pen: PenInventoryFields): boolean =>
  isFlagSet(pen.fields?.['Deactivated']);

/** Pens excluded from pickers and site-tab counts (deleted or deactivated). */
export const isSelectablePen = (pen: PenInventoryFields): boolean =>
  !isDeactivatedPen(pen) && !isDeletedPen(pen);

export const siteMetaFromInventoryRepoPlus = (
  inventoryPlus: InventoryRepoPlus,
  notAvailableLabel: string
): { siteKey: string; siteTitle: string } => {
  const { siteInfo } = inventoryPlus;
  if (!siteInfo) return { siteKey: `title:${notAvailableLabel}`, siteTitle: notAvailableLabel };
  const siteName = siteInfo.siteName?.trim();
  const inventorySiteName = siteInfo.name?.trim();
  const inventorySiteId = siteInfo.id?.trim();
  const hasNumericSiteId = typeof siteInfo.siteId === 'number' && siteInfo.siteId >= 0;

  const siteTitle =
    siteName ||
    inventorySiteName ||
    (hasNumericSiteId ? `Site ${siteInfo.siteId}` : '') ||
    notAvailableLabel;

  const siteKey = inventorySiteId ? `siteId:${inventorySiteId}` : `title:${siteTitle}`;

  return { siteKey, siteTitle };
};
